import React from "react";
import QuickPayButton from "./QuickPayButton";
import { getCartBundlePricing } from "../utils/cartBundle.js";
import { formatProductPrice } from "../utils.js";
import { ReactComponent as SizingModalCloseIcon } from "./../assets/svgs/sizing-modal-close-icon.svg";
import toast from "react-hot-toast";
import {
  getExclusiveVariantForSize,
  normalizeStockQuantity,
  getExclusiveVariantPricing,
  hasAvailableExclusiveVariant,
  buildExclusiveCartProduct,
} from "./../utils/exclusiveBundle.js";

const defaultCopy = {
  modalExpressCheckout: "Express Checkout",
  modalCompleteCollection: "Complete Your Collection",
  modalSelectSizes: "Select your sizes",
  modalApplePayDescription:
    "Choose a size for each available piece, then continue with Apple Pay.",
  modalCartDescription:
    "Choose a size for each available piece before adding the collection to your cart.",
  modalSelectSize: "Select size",
  modalContinue: "Select All Sizes To Continue",
  modalSoldOut: "Collection Sold Out",
  modalSelectEveryProduct: "Please select a size for every available product.",

  modalSoldOutSkipped: (count) =>
    `${count} ${
      count === 1 ? "product is" : "products are"
    } sold out and will be skipped.`,

  modalNoSizes: "No sizes are currently available.",

  modalSelectedCount: (selected, total) =>
    `${selected} of ${total} available ${
      total === 1 ? "product" : "products"
    } selected`,

  modalAddToCart: (count) =>
    `Add ${count} ${count === 1 ? "Product" : "Products"} To Cart`,

  modalClose: "Close sizing modal",
};

const ExclusiveBundleSizingModal = ({
  isOpen,
  products = [],
  bundle,
  copy = defaultCopy,
  mode = "cart",
  onClose,
  onConfirm,
}) => {
  const [selectedSizes, setSelectedSizes] = React.useState({});

  React.useEffect(() => {
    if (!isOpen) return;

    setSelectedSizes({});
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const availableProducts = products.filter(hasAvailableExclusiveVariant);

  const soldOutProducts = products.filter(
    (product) => !hasAvailableExclusiveVariant(product),
  );

  const selections = availableProducts.map((product) => {
    const selectedSize = selectedSizes[product.id];
    const selectedVariant = getExclusiveVariantForSize(product, selectedSize);

    return {
      product,
      selectedSize,
      variant: selectedVariant,
    };
  });

  const selectedCount = selections.filter(({ variant }) => {
    if (!variant) return false;

    const quantity = normalizeStockQuantity(variant?.quantity);

    return (
      variant?.isOutOfStock !== true && (quantity === null || quantity > 0)
    );
  }).length;

  const allSizesSelected =
    availableProducts.length > 0 &&
    selections.every(({ variant }) => {
      if (!variant) return false;

      const quantity = normalizeStockQuantity(variant?.quantity);

      return (
        variant?.isOutOfStock !== true && (quantity === null || quantity > 0)
      );
    });

  const quickPayBundleData = (() => {
    if (
      mode !== "applePay" ||
      !allSizesSelected ||
      !bundle?.id ||
      selections.length === 0
    ) {
      return {
        bundles: [],
        amount: 0,
      };
    }

    const bundleItems = [];
    const variantIds = [];

    for (const { product, variant } of selections) {
      const cartProduct = buildExclusiveCartProduct(product, variant);

      if (!cartProduct) return { bundles: [], amount: 0 };
      const variantId = Number(variant.id);
      if (!Number.isFinite(variantId) || variantId <= 0)
        return { bundles: [], amount: 0 };
      variantIds.push(variantId);

      bundleItems.push({
        product: cartProduct,
        quantity: 1,
      });
    }

    const numericBundleId = Number(bundle.id);
    if (!Number.isFinite(numericBundleId) || numericBundleId <= 0)
      return { bundles: [], amount: 0 };

    const pricing = getCartBundlePricing({
      id: String(bundle.id),
      name: bundle.name,
      discountPercent: bundle.discountPercent,
      quantity: 1,
      items: bundleItems,
    });

    return {
      bundles: [
        {
          bundle_id: numericBundleId,
          quantity: 1,
          variant_ids: variantIds,
        },
      ],
      amount: Math.round(Number(pricing?.total || 0) * 100),
    };
  })();
  const handleSizeSelection = (productId, size) => {
    setSelectedSizes((current) => ({
      ...current,
      [productId]: size,
    }));
  };

  const handleConfirm = () => {
    if (!allSizesSelected) {
      toast.error(copy.modalSelectEveryProduct);
      return;
    }
    onConfirm(selections);
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-3 py-5 backdrop-blur-[2px] sm:px-5"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="collection-sizing-title"
        className="flex max-h-[90vh] w-full max-w-[700px] flex-col overflow-hidden rounded-[12px] bg-white shadow-2xl"
      >
        {/* HEADER */}
        <div className="flex items-start justify-between border-b border-[#E5E7EB] px-5 py-5 sm:px-7">
          <div>
            <p className="font-spaceMono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#707070] sm:text-[11px] mt-[6px]">
              {mode === "applePay"
                ? copy.modalExpressCheckout
                : copy.modalCompleteCollection}
            </p>
            <h2
              id="collection-sizing-title"
              className="mt-1 font-inter text-[22px] font-bold tracking-[-0.02em] text-[#111111] sm:text-[26px]"
            >
              {copy.modalSelectSizes}
            </h2>
            <p className="font-helvena text-[12px] leading-[1.4] text-[#707070] sm:text-[13px]">
              {mode === "applePay"
                ? copy.modalApplePayDescription
                : copy.modalCartDescription}
            </p>
            {soldOutProducts.length > 0 && (
              <p className="mt-2 font-spaceMono text-[12px] leading-[1.5] text-[#B3261E] sm:text-[13px]">
                {copy.modalSoldOutSkipped(soldOutProducts.length)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={copy.modalClose}
            className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[24px] font-light leading-none text-[#111827] transition-colors hover:bg-[#F3F4F6]"
          >
            <SizingModalCloseIcon />
          </button>
        </div>

        {/* PRODUCTS */}
        <div className="overflow-y-auto px-5 sm:px-7">
          {availableProducts.map((product) => {
            const selectedSize = selectedSizes[product.id];
            const selectedVariant = getExclusiveVariantForSize(
              product,
              selectedSize,
            );
            const selectedPricing = getExclusiveVariantPricing(
              product,
              selectedVariant,
            );
            const displayedPrice = formatProductPrice(
              selectedPricing?.discountedPrice ??
                product?.discounted_price ??
                product?.price,
            );

            return (
              <div
                key={product.id}
                className="border-b border-[#E6E4E0] py-5 last:border-b-0"
              >
                <div className="flex gap-4">
                  {/* IMAGE */}
                  <div className="relative h-[92px] w-[72px] shrink-0 overflow-hidden rounded-[4px] bg-[#F3F0EA] sm:h-[128px] sm:w-[96px]">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>

                  {/* DETAILS */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-inter text-[12px] font-medium uppercase leading-[1.3] text-[#111111] sm:text-[14px]">
                          {product.displayId} — {product.name}
                        </p>

                        <p className="mt-1 font-spaceMono text-[12px] text-[#707070]">
                          {copy.modalSelectSize}
                        </p>
                      </div>

                      <span className="shrink-0 font-inter font-medium text-[12px] text-[#111111] sm:text-[14px]">
                        {displayedPrice}
                      </span>
                    </div>

                    {/* SIZES */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {product.sizes?.length > 0 ? (
                        product.sizes.map((size) => {
                          const variant = getExclusiveVariantForSize(
                            product,
                            size,
                          );
                          const quantity = normalizeStockQuantity(
                            variant?.quantity,
                          );
                          const isOutOfStock =
                            !variant ||
                            variant?.isOutOfStock === true ||
                            (quantity !== null && quantity <= 0);
                          const isSelected = selectedSize === size;

                          return (
                            <button
                              key={size}
                              type="button"
                              disabled={isOutOfStock}
                              aria-pressed={isSelected}
                              onClick={() =>
                                handleSizeSelection(product.id, size)
                              }
                              className={[
                                "relative flex h-[32px] md:h-[36px] min-w-[44px] md:min-w-[48px] items-center justify-center rounded-[4px] border px-3 text-[11px] md:text-[13px] font-inter transition-colors",
                                isSelected
                                  ? "border-black bg-black text-white font-semibold"
                                  : "border-[#E6E4E0] bg-white text-[#111111] font-normal",
                                !isOutOfStock ? "hover:border-black" : "",
                                isOutOfStock
                                  ? "cursor-not-allowed opacity-35 font-normal text-[#8E8D8A] border-[#E6E4E0]"
                                  : "",
                              ].join(" ")}
                            >
                              {size}
                            </button>
                          );
                        })
                      ) : (
                        <p className="font-inter text-[11px] text-[#A64346]">
                          {copy.modalNoSizes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* FOOTER */}
        <div className="border-t border-[#E5E7EB] bg-white px-5 py-4 sm:px-7 sm:py-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="font-spaceMono text-[11px] text-[#707070] sm:text-[13px]">
              {copy.modalSelectedCount(selectedCount, availableProducts.length)}
            </p>
          </div>

          {availableProducts.length > 0 ? (
            mode === "applePay" ? (
              allSizesSelected &&
              quickPayBundleData.bundles.length > 0 &&
              quickPayBundleData.amount > 0 ? (
                <QuickPayButton
                  express
                  className="w-full [&_[role=status]]:mt-0"
                  items={[]}
                  bundles={quickPayBundleData.bundles}
                  amount={quickPayBundleData.amount}
                  buttonHeight={44}
                  buttonRadius={5}
                  applePayTheme="black"
                />
              ) : (
                <button
                  type="button"
                  disabled
                  className="flex h-[44px] w-full cursor-not-allowed items-center justify-center rounded-[5px] border border-[#D1D5DB] bg-[#E5E7EB] font-spaceMono text-[11px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF] sm:text-[12px]"
                >
                  {copy.modalContinue}
                </button>
              )
            ) : (
              <button
                type="button"
                disabled={!allSizesSelected}
                onClick={handleConfirm}
                className={[
                  "flex h-[44px] w-full items-center justify-center rounded-[5px] border font-spaceMono text-[11px] font-bold uppercase tracking-[0.16em] transition-colors sm:text-[13px]",
                  allSizesSelected
                    ? "border-black bg-black text-white hover:opacity-90"
                    : "cursor-not-allowed border-[#D1D5DB] bg-[#E5E7EB] text-[#9CA3AF]",
                ].join(" ")}
              >
                {copy.modalAddToCart(availableProducts.length)}
              </button>
            )
          ) : (
            <button
              type="button"
              disabled
              className="flex h-[44px] w-full cursor-not-allowed items-center justify-center rounded-[5px] border border-[#D1D5DB] bg-[#E5E7EB] font-spaceMono text-[11px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF] sm:text-[12px]"
            >
              {copy.modalSoldOut}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExclusiveBundleSizingModal;
